import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import UserDirectoryTable from './components/UserDirectoryTable';
import UserDetailPanel from './components/UserDetailPanel';
import BulkOperationsPanel from './components/BulkOperationsPanel';
import FilterBar from './components/FilterBar';

const AdminUserManagement = () => {
  const navigate = useNavigate();
  const { isAdmin, loading: authLoading } = useAuth();
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    role: 'all',
    verificationStatus: 'all',
    searchTerm: ''
  });
  const [stats, setStats] = useState({
    totalUsers: 0,
    verifiedUsers: 0,
    buyers: 0,
    travelers: 0,
    admins: 0
  });

  // Redirect non-admin users
  useEffect(() => {
    if (!authLoading && !isAdmin) {
      navigate('/unified-dashboard');
    }
  }, [isAdmin, authLoading, navigate]);

  // Fetch all users
  const fetchUsers = async () => {
    setLoading(true);
    try {
      let query = supabase?.from('user_profiles')?.select('*')?.order('created_at', { ascending: false });

      // Apply filters
      if (filters?.role !== 'all') {
        query = query?.eq('role', filters?.role);
      }
      if (filters?.verificationStatus === 'verified') {
        query = query?.eq('is_verified', true);
      } else if (filters?.verificationStatus === 'unverified') {
        query = query?.eq('is_verified', false);
      }
      if (filters?.searchTerm) {
        query = query?.or(`full_name.ilike.%${filters?.searchTerm}%,email.ilike.%${filters?.searchTerm}%`);
      }

      const { data, error } = await query;
      if (error) throw error;

      setUsers(data || []);

      // Calculate stats
      const totalUsers = data?.length || 0;
      const verifiedUsers = data?.filter(u => u?.is_verified)?.length || 0;
      const buyers = data?.filter(u => u?.role === 'buyer')?.length || 0;
      const travelers = data?.filter(u => u?.role === 'traveler')?.length || 0;
      const admins = data?.filter(u => u?.role === 'admin')?.length || 0;

      setStats({ totalUsers, verifiedUsers, buyers, travelers, admins });
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin, filters]);

  const handleUserSelect = (user) => {
    setSelectedUser(user);
  };

  const handleBulkSelect = (userId) => {
    setSelectedUsers(prev => 
      prev?.includes(userId) 
        ? prev?.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    if (selectedUsers?.length === users?.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(users?.map(u => u?.id));
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    try {
      const { error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', userId);

      if (error) throw error;
      await fetchUsers();
      if (selectedUser?.id === userId) {
        setSelectedUser({ ...selectedUser, ...updates });
      }
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleBulkOperation = async (operation, value) => {
    try {
      const updates = {};
      if (operation === 'verify') updates.is_verified = true;
      if (operation === 'unverify') updates.is_verified = false;
      if (operation === 'changeRole') updates.role = value;

      const { error } = await supabase?.from('user_profiles')?.update(updates)?.in('id', selectedUsers);

      if (error) throw error;
      await fetchUsers();
      setSelectedUsers([]);
    } catch (error) {
      console.error('Error performing bulk operation:', error);
    }
  };

  if (authLoading || !isAdmin) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-20 px-6 pb-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
                  User Management
                </h1>
                <p className="text-muted-foreground">
                  Comprehensive oversight and control of all platform users
                </p>
              </div>
              <Button
                variant="outline"
                iconName="RefreshCw"
                onClick={fetchUsers}
                loading={loading}
              >
                Refresh
              </Button>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="bg-card rounded-lg p-4 shadow-elevation-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Icon name="Users" size={20} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.totalUsers}</p>
                    <p className="text-xs text-muted-foreground">Total Users</p>
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 shadow-elevation-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                    <Icon name="BadgeCheck" size={20} className="text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.verifiedUsers}</p>
                    <p className="text-xs text-muted-foreground">Verified</p>
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 shadow-elevation-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                    <Icon name="ShoppingBag" size={20} className="text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.buyers}</p>
                    <p className="text-xs text-muted-foreground">Buyers</p>
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 shadow-elevation-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                    <Icon name="Plane" size={20} className="text-purple-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.travelers}</p>
                    <p className="text-xs text-muted-foreground">Travelers</p>
                  </div>
                </div>
              </div>
              <div className="bg-card rounded-lg p-4 shadow-elevation-sm">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-destructive/10 flex items-center justify-center">
                    <Icon name="Shield" size={20} className="text-destructive" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats?.admins}</p>
                    <p className="text-xs text-muted-foreground">Admins</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <FilterBar filters={filters} setFilters={setFilters} />

          {/* Bulk Operations */}
          {selectedUsers?.length > 0 && (
            <BulkOperationsPanel
              selectedCount={selectedUsers?.length}
              onBulkOperation={handleBulkOperation}
              onClearSelection={() => setSelectedUsers([])}
            />
          )}

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* User Directory */}
            <div className="lg:col-span-2">
              <UserDirectoryTable
                users={users}
                loading={loading}
                selectedUsers={selectedUsers}
                onUserSelect={handleUserSelect}
                onBulkSelect={handleBulkSelect}
                onSelectAll={handleSelectAll}
              />
            </div>

            {/* User Detail Panel */}
            <div className="lg:col-span-1">
              {selectedUser ? (
                <UserDetailPanel
                  user={selectedUser}
                  onUpdate={handleUpdateUser}
                  onClose={() => setSelectedUser(null)}
                />
              ) : (
                <div className="bg-card rounded-lg p-8 shadow-elevation-md text-center">
                  <Icon name="UserCircle" size={64} className="text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Select a user to view details
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserManagement;